"use client"

import { useMemo } from "react"
import { useQueries } from "@tanstack/react-query"
import { formatDistanceToNow } from "date-fns"

import { asArray } from "@/lib/as-array"
import {
  getGetBucketsBucketIdContactsQueryOptions,
  useGetBuckets,
  useGetCompanies,
  useGetContacts,
  useGetTouchpointsDue,
} from "openapi/api/endpoints"
import type {
  Bucket,
  Company,
  Contact,
  GetTouchpointsDue200Item,
} from "openapi/api/model"

export type CompanyRow = {
  id: string
  name: string
  industry?: string
  /** "Stockholm · 240 employees", already joined. */
  where?: string
  website?: string
  contacts: number
  jobs: number
  /** "2 days ago" — a machine value, so it renders mono. */
  added?: string
}

/** A company as a row. The counts come from the caller, which already has them. */
export function toCompanyRow(
  company: Company,
  counts: { contacts_count?: number; jobs_count?: number },
): CompanyRow {
  const where = [company.location, company.size && `${company.size} employees`]
    .filter(Boolean)
    .join(" · ")

  return {
    id: company.id,
    name: company.name,
    industry: company.industry ?? undefined,
    where: where || undefined,
    website: company.website ?? undefined,
    contacts: counts.contacts_count ?? 0,
    jobs: counts.jobs_count ?? 0,
    added: since(company.created_at),
  }
}

export type BucketRow = {
  id: string
  name: string
  description?: string
  status: string
  /** Undefined while the per-bucket membership is still loading. */
  contacts?: number
  /** "every 14 days" */
  interval?: string
  pastDue: number
  /** 0–1: the share of the bucket that is past its interval. */
  pastDueShare: number
  updated?: string
}

/** "every 14 days", "weekly", "monthly" — the interval as a person says it. */
function intervalOf(days?: number) {
  if (!days || days <= 0) return undefined
  if (days === 1) return "daily"
  if (days === 7) return "weekly"
  if (days === 14) return "fortnightly"
  if (days === 30 || days === 31) return "monthly"
  if (days % 7 === 0) return `every ${days / 7} weeks`
  return `every ${days} days`
}

function since(iso?: string | null) {
  if (!iso) return undefined
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return undefined
  return formatDistanceToNow(date, { addSuffix: true })
}

/**
 * Everything the four Community tabs count, and the two tables that changed.
 *
 * Turn 17's framing is that the question here "is never who is in here but who
 * has gone quiet", so the figures the page leads with are the ones about being
 * overdue: `/touchpoints/due` is what makes "11 past due" and a bucket's own
 * past-due count real rather than decorative.
 */
export function useCommunity() {
  const companies = useGetCompanies()
  const contacts = useGetContacts()
  const buckets = useGetBuckets()
  const due = useGetTouchpointsDue()

  const companyList = useMemo(
    () => asArray<Company>((companies.data as { items?: unknown } | undefined)?.items),
    [companies.data],
  )

  const bucketList = useMemo(() => asArray<Bucket>(buckets.data), [buckets.data])
  const contactList = useMemo(() => asArray<Contact>(contacts.data), [contacts.data])
  const dueList = useMemo(
    () => asArray<GetTouchpointsDue200Item>(due.data),
    [due.data],
  )

  /*
    A bucket's size is only knowable one bucket at a time — nothing returns the
    memberships in bulk — so this is a query per bucket rather than a number
    invented from the ones already on screen.
  */
  const membership = useQueries({
    queries: bucketList
      .filter((bucket) => bucket.id)
      .map((bucket) =>
        getGetBucketsBucketIdContactsQueryOptions(bucket.id as string),
      ),
  })

  const sizeByBucket = useMemo(() => {
    const sizes = new Map<string, number>()
    bucketList
      .filter((bucket) => bucket.id)
      .forEach((bucket, index) => {
        const result = membership[index]
        if (result?.data) {
          sizes.set(bucket.id as string, asArray<Contact>(result.data).length)
        }
      })
    return sizes
  }, [bucketList, membership])

  /*
    Past due counts *people*, not messages. `/touchpoints/due` returns every due
    unsent touchpoint, and one contact can hold several in the same bucket — so
    counting rows overstates the figure and can push the coverage bar past the
    size of the bucket it is drawn against.
  */
  const dueByBucket = useMemo(() => {
    const seen = new Map<string, Set<string>>()
    for (const touchpoint of dueList) {
      if (!touchpoint.bucket_id) continue
      const who =
        touchpoint.bucket_contact_id ??
        `${touchpoint.bucket_id}:${touchpoint.contact_id ?? touchpoint.touchpoint_id ?? ""}`
      const inBucket = seen.get(touchpoint.bucket_id) ?? new Set<string>()
      inBucket.add(who)
      seen.set(touchpoint.bucket_id, inBucket)
    }
    return new Map([...seen].map(([bucket, people]) => [bucket, people.size]))
  }, [dueList])

  /** The same people, across every bucket — the figure the header leads with. */
  const pastDuePeople = useMemo(() => {
    const people = new Set<string>()
    for (const touchpoint of dueList) {
      people.add(
        touchpoint.bucket_contact_id ??
          `${touchpoint.bucket_id ?? ""}:${touchpoint.contact_id ?? touchpoint.touchpoint_id ?? ""}`,
      )
    }
    return people.size
  }, [dueList])

  const bucketRows = useMemo<BucketRow[]>(
    () =>
      bucketList
        .filter((bucket) => bucket.id)
        .map((bucket) => {
          const size = sizeByBucket.get(bucket.id as string)
          const pastDue = dueByBucket.get(bucket.id as string) ?? 0
          return {
            id: bucket.id as string,
            name: bucket.name ?? "Untitled bucket",
            description: bucket.description ?? undefined,
            status: bucket.status ?? "active",
            contacts: size,
            interval: intervalOf(bucket.interval_days),
            pastDue,
            pastDueShare: size && size > 0 ? Math.min(1, pastDue / size) : 0,
            updated: since(bucket.updated_at ?? bucket.created_at),
          }
        }),
    [bucketList, dueByBucket, sizeByBucket],
  )

  return {
    bucketRows,
    /** The four figures the tabs carry, and the two the header leads with. */
    tabCounts: {
      companies: companyList.length,
      contacts: contactList.length,
      buckets: bucketList.length,
      approvals: dueList.filter((touchpoint) => !touchpoint.approved_by).length,
    },
    totalContacts: contactList.length,
    pastDue: pastDuePeople,
    /*
      Whether the header's figures are known at all. Every list here falls back
      to empty, so while these are in flight — or after they fail — the header
      asserted "0 contacts" about an organization with thousands.
    */
    isCounting:
      companies.isLoading || contacts.isLoading || buckets.isLoading || due.isLoading,
    isCountsError:
      companies.isError || contacts.isError || buckets.isError || due.isError,
    isLoadingCompanies: companies.isLoading,
    /*
      A bucket row is three queries deep: the list, the memberships, and the due
      touchpoints. Reporting only the first lets the table leave its loading
      state and show "—" contacts and "all current" coverage, both of which read
      as facts rather than as "not known yet".
    */
    isLoadingBuckets:
      buckets.isLoading ||
      due.isLoading ||
      membership.some((result) => result.isLoading),
  }
}
